<script setup>
/**
 * Delete Office Hours — the same card as a session's (DeleteCard, XN-5114;
 * prototype `ohDeleteModal`). It says what goes: the open desk, Join the call
 * in every policyholder's portal, the hours on the calendar, and — when
 * anybody is waiting — how many, and that they are sent away. And that it
 * can be set up again, which deleting is what allows (one Office Hours per
 * hub). Nobody registers for Office Hours and it has no dates to choose
 * between, so neither is asked.
 */
import axios from 'axios';
import { computed, ref } from 'vue';
import DeleteCard from './DeleteCard.vue';

const props = defineProps({
  // Who is in the queue now, as the page last heard it.
  waiting: { type: Number, default: 0 },
});

const sentAway = computed(() => (props.waiting > 0
  ? ` ${props.waiting} ${props.waiting === 1 ? 'person is' : 'people are'} waiting right now — they are sent away.`
  : ''));

const emit = defineEmits(['close', 'deleted']);

const busy = ref(false);
const failure = ref('');

const confirm = async () => {
  busy.value = true;
  failure.value = '';
  try {
    const { data } = await axios.delete(`${window.store.event_backend_api}/office-hours`);
    emit('deleted', data?.data ?? {});
  } catch (error) {
    // Already gone (another tab, another person): the end state wanted.
    if (error?.response?.status === 404) {
      emit('deleted', {});
      return;
    }
    failure.value = error?.response?.data?.message || "That didn't work. Try again.";
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <DeleteCard
    title="Delete Office Hours?"
    keep-label="Keep Office Hours"
    :busy="busy"
    data-testid="delete-office-hours"
    @close="emit('close')"
    @confirm="confirm"
  >
    <template #line>
      <span data-testid="delete-office-hours-what">The open desk closes. <b>Join the call</b> disappears from every
        policyholder's portal, and the hours come off the calendar.{{ sentAway }}</span>
    </template>

    <p class="delete-card__facts">You can set up Office Hours again later. Nothing else about your sessions changes.</p>
    <p v-if="failure" class="delete-card__failure" role="alert" data-testid="delete-office-hours-failure">{{ failure }}</p>
  </DeleteCard>
</template>
